import { Component, Input, ViewChild, ElementRef, AfterViewChecked, ViewEncapsulation, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import {casefloDateFormat, CUSTOM_DATE_FORMATS, CustomDatePickerAdapter} from '../../../models/date-formats.model';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import {MatCalendarCellCssClasses} from "@angular/material/datepicker";
import {MeetingModel} from "@api/models/meeting-model";

@Component({
  selector: 'cf2-date-field-top',
  template: `
    <style>
      .unavailable-date-partial {
        background-color: rgba(253, 220, 0, 0.9) !important;
        border-radius: 100% !important;
      }

      .unavailable-date-whole {
        background-color: rgba(255, 77, 0, 0.9) !important;
        border-radius: 100% !important;
        cursor: not-allowed !important;
        pointer-events: none !important;
      }

      .partial-day-warning {
        color: rgb(255, 0, 0, 1) !important;
        font-weight: 500 !important;
        font-size: small !important;
      }
    </style>
    <div
        #container
        [ngClass]="{
        'input-field-top-small': small && !fullWidth,
        'input-field-top': !small && !fullWidth,
        'full-width': fullWidth,
        'full-width-small': fullWidth && small
      }"
    >
      <mat-form-field appearance="standard" floatLabel="always" [hideRequiredMarker]="true" #elRef>
        <mat-label *ngIf="labelVisible" for="{{ idField }}">
          {{ label }} <span *ngIf="required === true" class="required-asterisk">*</span>
        </mat-label>
        <input
            #dateInput
            matInput
            [matDatepicker]="datepicker"
            id="{{ idField }}"
            name="{{ idField }}"
            placeholder="{{ placeHolder }}"
            value="{{ value }}"
            [min]="minDate"
            [max]="maxDate"
            autocomplete="off"
            (dateChange)="onDateSelected()"
            (blur)="isDateValid()"
            (input)="updateDate($event.target.value)"
            (change)="updateDate($event.target.value)"
            [formControl]="ctrl"
        />

        <mat-datepicker-toggle matSuffix [for]="datepicker" [disabled]="disabled" #date></mat-datepicker-toggle>
        <mat-datepicker
            [dateClass]="dateClass()"
            startView="month" #datepicker
        ></mat-datepicker>
        <mat-hint *ngIf="isPartialDate" class="partial-day-warning">{{ 'PartialDayUnavailable' | transloco }}<br/>{{ unavailableHours }}</mat-hint>
        <mat-error *ngIf="hasError(ctrl)">{{ hasCustomError ? errorText : errorMap(ctrl) }}</mat-error>
        <!-- <mat-error *ngIf="hasCustomError">abcdef {{ errorText }}</mat-error> -->
      </mat-form-field>
    </div>
  `,
  styleUrls: ['../input-field-top.component.scss'],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    {
      provide: DateAdapter,
      useClass: CustomDatePickerAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class DateFieldTopComponent implements AfterViewChecked, OnChanges {
  @ViewChild('dateInput') input: ElementRef;
  @ViewChild('container') container: ElementRef;
  @Input() hasCustomError = false;
  @Input() label: string;
  @Input() ctrl = new UntypedFormControl();
  @Input() required = false;
  @Input() maxDate: Date;
  @Input() minDate: Date;
  @Input() defaultDate = new Date(Date.now());
  @Input() errorText: string;
  @Input() idField: string;
  @Input() placeHolder = casefloDateFormat;
  @Input() value = '';
  @Input() small = false;
  @Input() disabled = false;
  @Input() fullWidth = false;
  @Input() labelVisible = true;
  @Input() readOnly = false;
  @Input() unavailableDates = []
  
  @Output() notifyHours = new EventEmitter();

  private trackDateValue = '';
  protected isPartialDate = false;
  protected unavailableHours = '';

  public ngAfterViewChecked(): void {
    if (this.value !== undefined && this.value.length > 0) this.updateValue();
    if (this.disabled) this.setDisabled();
    if (this.maxDate !== undefined) this.updateMax;
    if (this.minDate !== undefined) this.updateMin;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['unavailableDates']) {
      this.onDateSelected();
    }
  }

  dateClass() {
    return (date: Date): MatCalendarCellCssClasses => {
      if (!this.unavailableDates || this.unavailableDates.length === 0 || !Array.isArray(this.unavailableDates)) return '';
      
      const meeting = this.getUnavailableDate(date);

      if (meeting) {
        const startTime = new Date(meeting.startTime).getHours();
        const endTime = new Date(meeting.endTime).getHours();
        if (startTime < 10 && endTime > 16) {
          this.notifyHours.emit({});
          this.unavailableHours = '';
          return 'unavailable-date-whole';
        }
        else {
          return 'unavailable-date-partial';
        }
      }
      this.notifyHours.emit({});
      this.unavailableHours = '';
      return '';
    };
  }

  public onDateSelected(): void {
    if (!this.unavailableDates || this.unavailableDates.length === 0 || !Array.isArray(this.unavailableDates)) {
      this.notifyHours.emit({});
      this.unavailableHours = '';
      this.isPartialDate = false;
      return;
    }

    if (this.ctrl.value) {
      const meeting = this.getUnavailableDate(new Date(this.ctrl.value));

      if (meeting) {
        let startTime = new Date(meeting.startTime).getHours();
        startTime = startTime + (new Date(meeting.startTime).getMinutes() / 100);
        let endTime = new Date(meeting.endTime).getHours();
        endTime = endTime + (new Date(meeting.endTime).getMinutes() / 100);

        this.unavailableHours = meeting.startTimeFormatted + ' - ' + meeting.endTimeFormatted;
        this.notifyHours.emit({
          startTime: startTime,
          startTimeFormatted: meeting.startTimeFormatted,
          endTime: endTime,
          endTimeFormatted: meeting.endTimeFormatted,
        });
        this.isPartialDate = true;
      }
      else {
        this.notifyHours.emit({});
        this.unavailableHours = '';
        this.isPartialDate = false;        
      }
    }
  }
  
  private getUnavailableDate(currentDate: Date): MeetingModel {
    return this.unavailableDates.find(unavailableItem => {
      const unavailableDate = new Date(unavailableItem.recurrenceStartDate);

      return unavailableDate.getDate() === currentDate.getDate() &&
          unavailableDate.getMonth() === currentDate.getMonth() &&
          unavailableDate.getFullYear() === currentDate.getFullYear();
    });
  }
  
  public updateValue(): void {
    const input: HTMLInputElement = this.input.nativeElement as HTMLInputElement;
    input.value = this.value;
    this.trackDateValue = this.value.toString();
  }

  public updateMax(): void {
    const input: HTMLInputElement = this.input.nativeElement as HTMLInputElement;
    input.max = this.maxDate.toDateString();
  }

  public updateMin(): void {
    const input: HTMLInputElement = this.input.nativeElement as HTMLInputElement;
    input.min = this.minDate.toDateString();
  }

  public setDisabled(): void {
    const input: HTMLInputElement = this.input.nativeElement as HTMLInputElement;
    input.disabled = true;

    // update class
    const container: HTMLInputElement = this.container.nativeElement as HTMLInputElement;
    container.classList.add('disabled');
  }

  public setFocus(): void {
    const input: HTMLInputElement = this.input.nativeElement as HTMLInputElement;
    input.focus();
  }

  public select(): void {
    const input: HTMLInputElement = this.input.nativeElement as HTMLInputElement;
    input.select();
  }

  public errorMap(fc: UntypedFormControl): string {
    //debugger;

    if (fc.errors === undefined || Object.keys(fc.errors).length < 1) return null;
    if (fc.errors.matDatepickerMin) return 'Too far in the past.';
    if (fc.errors.minDate) return 'Too far in the past.';
    if (fc.errors.matDatepickerMax) return 'Too far in the future.';
    if (fc.errors.maxDate) return 'Too far in the future.';
    if (fc.errors.required) return 'This field is required.';
    if (fc.errors.invalidDate) return 'Invalid date. Please use format ' + casefloDateFormat + '.';

    return 'Invalid date. Please use format ' + casefloDateFormat + '.';
  }

  // pickup numeric keys only
  omitCharacters(event: KeyboardEvent): boolean {
    const k = event.charCode;
    return (k > 47 && k < 58) || k === 45 || k === 47;
  }

  // capture input value
  updateDate(value: string): void {
    this.trackDateValue = value;
  }

  // validate date on blur
  isDateValid(): boolean {
    if (this.ctrl.invalid && this.ctrl.value === null && this.trackDateValue.length > 0) {
      this.ctrl.setErrors({ invalidDate: true });
      return false;
    } else if (
      this.ctrl.value === null &&
      this.trackDateValue.length === 0 &&
      this.ctrl.errors !== null &&
      Object.keys(this.ctrl.errors).length > 0
    ) {
      this.ctrl.reset();
    }

    if (this.ctrl.value !== null) {
      this.ctrl.setValue(this.ctrl.value);
    }

    return true;
  }

  // validate the date
  public hasError(fc: UntypedFormControl): boolean {
    if (!fc || !fc.invalid) {
      return false;
    }
    if (this.hasCustomError) {
      return fc.invalid && fc.touched;
    }
    return fc.invalid && fc.touched && Object.keys(fc.errors).length > 0;
  }

  requireAllPlanItemFields() {}
}
